import { ProgramTypeName, type Person } from "@/types";
import type { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import type { Path } from "@/router";
import { buildLevelDateMetrics } from "@/features/person/utils/pgm-levels";
import { buildQualifyingLevelRequirements } from "@/features/person/utils/qualifyingLevelRequirements";
import { withMembershipApplicationLinks } from "@/features/person/utils/membershipApplicationLinks";
import type { RequirementRow } from "@/features/person/utils/pgm-requirements";
import { withBackgroundCheckRequirementLinks } from "@/features/person/components/BackgroundCheckOrders/backgroundCheckRequirementLinks";
import { QUALIFYING_LEVEL_REQUIREMENTS_SCROLL_ID } from "@/features/person/utils/membershipProgressLinks";
import type { PgmRequirementsSectionProps } from "./PgmRequirementsSection";

export const QUALIFYING_LEVEL_REQUIREMENTS_INTRO =
  "View all requirements and completion status for the Qualifying Level.";
export const QUALIFYING_LEVEL_PROSPECT_REQUIREMENTS_BODY =
  "View all requirements and completion status for the Qualifying Level below. Prospects applying to the Associate Program for Level 1 must have eligible employment, which is tracked as part of their application. Those applying to the Affiliate program can postpone eligible employment until Level 2.";

export function isProspectOrNoProgram(person: Person): boolean {
  const name = person.latestProgram?.programType?.name;
  return name == null || name === ProgramTypeName.Prospect;
}

export function qualifyingLevelRequirementsBody(person: Person): string {
  return isProspectOrNoProgram(person)
    ? QUALIFYING_LEVEL_PROSPECT_REQUIREMENTS_BODY
    : QUALIFYING_LEVEL_REQUIREMENTS_INTRO;
}

export type BuildQualifyingLevelRequirementsSectionOptions = {
  person: Person;
  pgaId: string;
  staffSectionVisible: boolean;
  canLinkApplications: boolean;
  isSuspended?: boolean;
  suspensionReason?: string;
  terminationTimeRemaining?: string | null;
  loading?: boolean;
};

export function buildQualifyingLevelRequirementsSectionProps(
  options: BuildQualifyingLevelRequirementsSectionOptions
): PgmRequirementsSectionProps {
  const {
    person,
    pgaId,
    staffSectionVisible,
    canLinkApplications,
    isSuspended = false,
    suspensionReason,
    terminationTimeRemaining,
    loading = false
  } = options;

  const qualifyingLevelRequirements = withBackgroundCheckRequirementLinks(buildQualifyingLevelRequirements(person), {
    staffSectionVisible,
    pgaId
  });

  const requirements: RequirementRow[] = canLinkApplications
    ? withMembershipApplicationLinks(qualifyingLevelRequirements, person)
    : qualifyingLevelRequirements;

  const dateMetrics: MetricCardType<Path>[] = buildLevelDateMetrics(person, 0, {
    isSuspended,
    suspensionReason,
    terminationTimeRemaining
  });

  return {
    sectionId: QUALIFYING_LEVEL_REQUIREMENTS_SCROLL_ID,
    heading: "Qualifying Level Requirements",
    body: qualifyingLevelRequirementsBody(person),
    dateMetrics,
    dateMetricsTestId: "qualifying-date-metrics",
    requirements,
    tableTestId: "qualifying-requirements-table",
    emptyTitle: "No Qualifying Level Requirements",
    emptyCaption: "Qualifying level requirements are not available for this person",
    loading
  };
}
